import { TIERS } from './tiers';
import { servicePackages } from './services';

/**
 * The three pricing categories and where each one lives. Navigation, the
 * category selector, the sitemap and the cross-links all read from here, so a
 * URL or a "from" price is stated once.
 *
 * Each category has its own page and its own search intent, on purpose:
 *   /pricing                       software plans for a managed website
 *   /marketing/pricing             managed marketing for a site you already have
 *   /growth-partnership/pricing    one team running website, software and marketing
 */

export type PricingCategoryId = 'software' | 'marketing' | 'partnership';

export type PricingCategory = {
  id: PricingCategoryId;
  href: string;
  /** Short label for menus and tabs. */
  label: string;
  /** Full name used in headings. */
  name: string;
  /** One line: who it is for. */
  forWho: string;
  /** "From $199/month", derived from the same configs the cards render. */
  from: string;
};

const usd = (n: number) => `$${n.toLocaleString('en-US')}`;

const softwareFrom = Math.min(...TIERS.flatMap((t) => (t.price ? [t.price] : [])));
const marketingFrom = Math.min(...servicePackages('marketing').map((p) => p.price));
const partnershipFrom = Math.min(...servicePackages('partnership').map((p) => p.price));

export const PRICING_CATEGORIES: PricingCategory[] = [
  {
    id: 'software',
    href: '/pricing',
    label: 'Website & software',
    name: 'Website and software plans',
    forWho: 'A managed website, the software behind it, and local SEO. First 30 days free.',
    from: `From ${usd(softwareFrom)}/month`,
  },
  {
    id: 'marketing',
    href: '/marketing/pricing',
    label: 'Managed marketing',
    name: 'Managed marketing',
    forWho: 'Keep your current website. We run your content and social channels.',
    from: `From ${usd(marketingFrom)}/month`,
  },
  {
    id: 'partnership',
    href: '/growth-partnership/pricing',
    label: 'Growth partnership',
    name: 'Complete growth partnership',
    forWho: 'One team runs your website, software, local SEO and marketing together.',
    from: `From ${usd(partnershipFrom)}/month`,
  },
];

export function pricingCategory(id: PricingCategoryId): PricingCategory {
  return PRICING_CATEGORIES.find((c) => c.id === id)!;
}
